/**
 * A BROWSER TAB RINGING FOR ITS AGENT, drawn as what it is.
 *
 * Owner, 2026-10-12: 在 CleanView 的狀態下, 我們需要 Render Browser Activity. 讓使用者可以從
 * 那個 browser hash 知道他的該 tab 的 title 以及是從哪裡過來的 — 這樣子使用者就很容易知道如何去
 * 追蹤這些對話裡面的訊息.
 *
 * "Notify agent" (the extension's switch on a tab) types this into the agent's input:
 *
 *   <browser-activity tab=t1746214120 browser="Brave on Mac">
 *   Something happened in this tab since you last looked: the page changed · the person clicked.
 *   Look: avengents browser snapshot --tab t1746214120
 *   You are told once per tab — the next notice comes after you look at it again.
 *   </browser-activity>
 *
 * It lands in the transcript as a `user` block, so Clean View drew it under the OWNER's
 * face with a "you" badge and the raw tag as its first line — forty-three times in one
 * real conversation. It is not the owner speaking and it is not a person at all.
 *
 * THE TITLE IS NOT IN THE NOTICE, ON PURPOSE, AND IT STAYS OUT. A notice is the one thing
 * of a tab our servers write down, so it carries nothing that was read from the page —
 * a label for the tab and the browser's name, never an address or a title (extension
 * core/watch.js, and the privacy policy says so in those words). Putting the title into
 * the line the agent reads would also put words a PAGE chose into the agent's input
 * without the agent having asked for them.
 *
 * SO IT IS READ FROM WHAT THE AGENT ITSELF WAS TOLD. Every `avengents browser` command
 * the agent runs answers with the tab's label first, and four of those answers name the
 * page on that first line:
 *
 *   [t12] Now at: "Title" — https://…            a navigation; a click that went somewhere
 *   [t12] Page: "Title"                          snapshot, text  (the address is line two)
 *   [t12] Screenshot of "Title" — https://…      (or: … of e5 button "x" on "Title" — …)
 *   Your tabs …  ⏎  t12 *  "Title"  https://…  (given by the owner; notices on)      `tabs`
 *
 * Those are tool results in the SAME conversation, on the user's own machine. A notice
 * takes the last thing the agent was told about that tab before it rang; if the agent
 * had not looked yet, the first thing it was told after. Nothing new leaves the browser
 * for this, and a tab the agent has never looked at shows its label and nothing more —
 * which is the truth: nobody on this side knows what it is yet.
 */
import { createContext } from 'react'
import type { TranscriptBlock } from '../transport/api'

export type BrowserActivity = {
  /** The tab's label, as the extension mints it (`t` + Chrome's tab id). */
  tab: string
  /** The browser's own name — "Brave on Mac". A person typed it, so it is shown as text. */
  browser: string
  /** What happened, in the notice's own words: "the page changed · the person clicked". */
  what: string
}

/** What the agent was told this tab is, and whether that was before the tab rang or after. */
export type TabFact = { title: string; url: string; when: 'before' | 'after' }

/** `t` + digits — the shape the cloud also insists on before it relays a notice. */
const LABEL = 't\\d{1,20}'
const OPEN = new RegExp(`^<browser-activity tab=(${LABEL}) browser="([^"<>\\n]{0,160})">\\n?`)
const CLOSE = '</browser-activity>'
const WHAT = /^Something happened in this tab since you last looked: (.+?)\.?$/m

/**
 * Read one `user` block as a tab's notice, or null if it is anything else.
 *
 * THE WHOLE BLOCK OR NOT AT ALL, anchored at its start (the rule `parseInbound` and
 * `systemNote` follow): a message that merely contains a notice — the owner quoting one,
 * an agent discussing this very feature, the summary a long conversation is continued
 * from — is a real message and keeps its author. So does a notice with anything typed
 * after it. (Line ends are read as the CLI recorded them; a `\r\n` transcript is the
 * same notice.)
 */
function parseBrowserActivity(text: string | null | undefined): BrowserActivity | null {
  const raw = (text ?? '').replace(/\r\n?/g, '\n')
  const open = OPEN.exec(raw)
  if (!open?.[1]) return null
  const rest = raw.slice(open[0].length)
  const close = rest.indexOf(CLOSE)
  if (close === -1 || rest.slice(close + CLOSE.length).trim()) return null
  const what = WHAT.exec(rest.slice(0, close))?.[1]?.trim()
  return {
    tab: open[1],
    browser: (open[2] ?? '').trim(),
    // A notice from a build that words it differently still happened; it is not given
    // words it did not say.
    what: what || 'something happened',
  }
}

/** Kept by identity: a block's text never changes once it exists. */
const noticed = new WeakMap<TranscriptBlock, BrowserActivity | null>()
/** The notice this block IS, read once. */
export function tabNotice(b: TranscriptBlock | null | undefined): BrowserActivity | null {
  if (!b || b.kind !== 'user') return null
  let act = noticed.get(b)
  if (act === undefined) {
    act = parseBrowserActivity(b.text)
    noticed.set(b, act)
  }
  return act
}

/**
 * A JSON string literal — how the extension quotes every title (`JSON.stringify`) — or
 * one whose line was CUT before it closed: `"Codex 中文社群 | 【這篇是 AI Agent 自己發的，不是真`.
 * Agents cut their output to sixty or a hundred and fifty characters (see `address`), and
 * a long title is where the cut lands. The second form is only ever the END of a line.
 */
const QUOTED = '"(?:[^"\\\\]|\\\\.)*(?:"|$)'
const AT_LABEL = new RegExp(`^\\[(${LABEL})\\] `)
const NOW_AT = new RegExp(`^Now at: (${QUOTED})(?: — (\\S+))?`)
const PAGE = new RegExp(`^Page: (${QUOTED})$`)
/** The LAST quoted thing before the dash is the page; an element's own name may come first. */
const SHOT = new RegExp(`^Screenshot of (?:.* on )?(${QUOTED})(?: — (\\S*))?`)
/** `open` answers on one line, the label inside it: `Opened t12. Now at: "Title" — https://…`. */
const OPENED = new RegExp(`^Opened (${LABEL})\\. Now at: (${QUOTED})(?: — (\\S+))?`)
/**
 * One row of `tabs`: the label, a star on the one in use, the title, the address, and who
 * gave the tab — of which a row cut at a hundred and fifty characters keeps only as much
 * as fits, so only the label and the title are asked of it.
 */
const TABS_ROW = new RegExp(`^(${LABEL})(?: \\*)?\\s+(${QUOTED})(?:\\s+(\\S+))?`)
const TABS_HEAD = /^Your tabs\b/
/** With tabs in several browsers the cloud names each above its rows: `— Brave on Mac … —`. */
const TABS_BROWSER = /^— .* —$/
/** …and names the browser above a single answer: `(in Chrome on Mac)`. */
const IN_BROWSER = /^\(in [^\n]*\)$/

/**
 * The title inside the quotes — with an ellipsis when the line was cut before they closed,
 * so a title that is not whole does not pass for one.
 */
function unquote(q: string): string | null {
  const whole = q.length > 1 && q.endsWith('"') && !/(?:^|[^\\])(?:\\\\)*\\"$/.test(q)
  // A cut may land inside an escape (`\u00e` or a lone backslash); what is left of it goes.
  const text = whole ? q : `${q.replace(/\\(?:u[0-9a-fA-F]{0,3})?$/, '')}"`
  try {
    const v: unknown = JSON.parse(text)
    if (typeof v !== 'string') return null
    if (whole) return v
    // CUT IN THE MIDDLE, NOT AT THE END (`cut -c1-40,120-`, and it is in the real data):
    // the quote that closed the title went with what was cut, and the rest of the line —
    // the tail of an address, "(given by the owner)" — would be read as title. A row's
    // fields are two spaces apart and an answer's address follows a dash; a "title" that
    // holds either is not one.
    if (/ {2}| — https?:|\((?:given by the owner|you opened it)/.test(v)) return null
    // (`cut -c` counts bytes in some locales and halves a character; the half goes.)
    const kept = v.replace(/\uFFFD+$/, '').trimEnd()
    return kept ? `${kept}…` : null
  } catch {
    return null
  }
}

/** Is `cut` the start of `whole` — the same title, with less of it? */
const cutOf = (cut: string, whole: string): boolean => cut.endsWith('…') && !whole.endsWith('…') && whole.startsWith(cut.slice(0, -1))

/**
 * An address whose SITE can be believed, or nothing.
 *
 * AGENTS CUT THEIR OWN OUTPUT — `… | tail -1 | cut -c1-60` is in a third of the real
 * commands — and a line cut inside the address leaves `https://www.facebook.co`, which
 * is another site's name. Chrome always gives an http address its slash after the host,
 * so an address without one was cut before the host ended, and is not shown at all: a
 * wrong site is worse than none. (A title cut short loses its closing quote and is not
 * read as a title, for the same reason. The extension says "(unknown)" when it has no
 * address; that is nothing too.)
 */
const address = (u: string | undefined): string => (u && /^https?:\/\/[^/\s]+\//i.test(u) ? u : '')

type Named = { tab: string; title: string; url: string }

/**
 * Every (tab, title, address) one tool result states.
 *
 * ONLY ITS FIRST LINE — and, for `tabs`, the rows straight under the heading that IS its
 * first line. A result is whatever one shell command printed, and agents chain and cut:
 * `scroll … | head -1; text … | tail -5` puts a PAGE's words on the line after our own.
 * The first cut of this believed "the one line after a label line", and the review of it
 * drew a row that said "Your Bank · bank.example" over a tab that was somewhere else —
 * with no knowledge of the tab's label — and drew tab B's page on tab A's rows. So:
 *
 *   · the FIRST line (the second, when the first is the cloud's "(in Chrome on Mac)")
 *     must open with the tab's label and state the page ON THAT LINE: `Now at:`,
 *     `Screenshot of`, `Opened t12. Now at:`, or `Page:` — whose address is on the next
 *     line and is therefore NOT read; a snapshot gives a title and no site;
 *   · a row of `tabs` counts while the lines since the heading are all rows (or the
 *     cloud's line naming a browser), and stops at the first line that is neither.
 *
 * What is left for a page to do: be the first line of a result — an agent that runs
 * `text … | tail -1` first — AND know the tab's label, which a tab is never told. (The
 * title was the page's to choose all along. It is the ADDRESS that has to be the
 * browser's word, and the extension takes that from the browser's navigation history,
 * never from the page — core/driver.js `where`.)
 */
export function tabsNamedIn(text: string | null | undefined): Named[] {
  const raw = (text ?? '').replace(/\r\n?/g, '\n')
  if (!/^(?:\[t|Your tabs|\(in |Opened t)/.test(raw)) return []
  const lines = raw.split('\n', 400)
  let i = IN_BROWSER.test(lines[0] ?? '') ? 1 : 0
  const first = lines[i] ?? ''

  if (TABS_HEAD.test(first)) {
    const out: Named[] = []
    for (i += 1; i < lines.length; i++) {
      const line = lines[i] ?? ''
      const row = TABS_ROW.exec(line)
      const title = row?.[2] ? unquote(row[2]) : null
      if (row?.[1] && title !== null) out.push({ tab: row[1], title, url: address(row[3]) })
      else if (!TABS_BROWSER.test(line)) break
    }
    return out
  }

  const opened = OPENED.exec(first)
  const tabOpened = opened?.[2] ? unquote(opened[2]) : null
  if (opened?.[1] && tabOpened !== null) return [{ tab: opened[1], title: tabOpened, url: address(opened[3]) }]

  const at = AT_LABEL.exec(first)
  if (!at?.[1]) return []
  const rest = first.slice(at[0].length)
  const named = NOW_AT.exec(rest) ?? SHOT.exec(rest)
  const title = named?.[1] ? unquote(named[1]) : null
  if (named && title !== null) return [{ tab: at[1], title, url: address(named[2]) }]
  const head = PAGE.exec(rest)?.[1]
  const page = head ? unquote(head) : null
  return page !== null ? [{ tab: at[1], title: page, url: '' }] : []
}

/**
 * WHICH TAB THE AGENT WORKED IN LAST — the label its own last browser command answered with:
 * `[t12] …` (every command on a tab answers with the tab first), or `Opened t12. …`. For the
 * corner of its session (AgentTabCorner), to show that tab when it holds more than one.
 *
 * Tool results only — a notice names a tab the PERSON touched — and never `tabs`, which
 * names every tab and touches none. Only a tab the agent holds is ever shown from this, so a
 * page that printed `[t9]` at the top of a result can at worst pick another of its own tabs.
 */
export function lastTabTouched(blocks: readonly TranscriptBlock[]): string | null {
  if (lastPerWindow.has(blocks)) return lastPerWindow.get(blocks) ?? null
  let found: string | null = null
  for (let i = blocks.length - 1; i >= 0 && found === null; i--) {
    const b = blocks[i]
    if (b?.kind === 'tool_result') found = touchedIn(b.text)
  }
  lastPerWindow.set(blocks, found)
  return found
}
const lastPerWindow = new WeakMap<readonly TranscriptBlock[], string | null>()
const OPENED_LABEL = new RegExp(`^Opened (${LABEL})\\.`)
function touchedIn(text: string | null | undefined): string | null {
  const raw = text ?? ''
  if (!/^(?:\[t|\(in |Opened t)/.test(raw)) return null
  const lines = raw.replace(/\r\n?/g, '\n').split('\n', 2)
  const first = (IN_BROWSER.test(lines[0] ?? '') ? lines[1] : lines[0]) ?? ''
  return AT_LABEL.exec(first)?.[1] ?? OPENED_LABEL.exec(first)?.[1] ?? null
}

/** Kept by identity, like the search's lowered text: a result is read once however often the window moves. */
const stated = new WeakMap<TranscriptBlock, Named[]>()
function statedIn(b: TranscriptBlock): Named[] {
  let n = stated.get(b)
  if (!n) {
    n = tabsNamedIn(b.text)
    stated.set(b, n)
  }
  return n
}

const perWindow = new WeakMap<readonly TranscriptBlock[], ReadonlyMap<TranscriptBlock, TabFact>>()

/**
 * For each notice in these blocks, what its tab was — keyed by the block itself, which is
 * the object the row is drawn from. Asked for by the panel and by the search of the same
 * window, and worked out once per window.
 *
 * ONE PASS FORWARD, ONE BACK. Forward, a notice takes the latest thing said about its tab
 * so far. A notice that found nothing (the tab rang before the agent ever looked) is
 * filled on the way back with the first thing said after it. Blocks are in the order
 * they happened, so position is the clock — `ts` is absent on two transcript sources.
 *
 * OF THE WINDOW THAT IS LOADED: a notice near its top may be shown with the first look
 * after it, and take the look before it when scrolling back loads that. Both are true
 * things the agent was told; the row says which (`when`).
 */
export function tabFacts(blocks: readonly TranscriptBlock[]): ReadonlyMap<TranscriptBlock, TabFact> {
  const done = perWindow.get(blocks)
  if (done) return done
  const facts = new Map<TranscriptBlock, TabFact>()
  perWindow.set(blocks, facts)
  const known = new Map<string, Named>()
  const waiting: { block: TranscriptBlock; tab: string }[] = []
  for (const b of blocks) {
    if (b.kind === 'tool_result') {
      for (const n of statedIn(b)) {
        // THE SAME PAGE, SAID WITH LESS OF ITS TITLE, does not replace the title already
        // known whole: the agent cut this answer shorter than the last one, the tab did
        // not change. (Its address, when this answer has one, is the newer word.)
        const was = known.get(n.tab)
        known.set(n.tab, was && cutOf(n.title, was.title) ? { ...was, url: n.url || was.url } : n)
      }
      continue
    }
    const act = tabNotice(b)
    if (!act) continue
    const fact = known.get(act.tab)
    if (fact) facts.set(b, { title: fact.title, url: fact.url, when: 'before' })
    else waiting.push({ block: b, tab: act.tab })
  }
  if (!waiting.length) return facts
  const later = new Map<string, Named>()
  let w = waiting.length - 1
  for (let i = blocks.length - 1; i >= 0 && w >= 0; i--) {
    const b = blocks[i]!
    if (b === waiting[w]!.block) {
      const fact = later.get(waiting[w]!.tab)
      if (fact) facts.set(b, { title: fact.title, url: fact.url, when: 'after' })
      w--
      continue
    }
    if (b.kind !== 'tool_result') continue
    // Read backwards, the FIRST statement in a result is the one that stands: it is the
    // earliest after the notice.
    const named = statedIn(b)
    for (let k = named.length - 1; k >= 0; k--) {
      const n = named[k]!
      // Going back in time: an EARLIER answer replaces a later one — unless the earlier
      // one is only a shorter cut of the same title.
      const was = later.get(n.tab)
      later.set(n.tab, was && cutOf(n.title, was.title) ? { ...was, url: n.url || was.url } : n)
    }
  }
  return facts
}

/** `www.facebook.com` → `facebook.com`; nothing for an address that is not one. */
function siteOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

/**
 * The notice's words, said to the READER.
 *
 * The notice is written for the agent, in the third person: "the person clicked". In
 * Clean View the reader IS that person, and is "you" on every other row. The four kinds
 * are a closed list (the cloud's NOTICE_KINDS); anything else — a newer service saying
 * something this build has not heard of — is shown in its own words rather than dropped.
 *
 * "It went to another page" needs the title beside it to mean something, and which page
 * that title is depends on where it was read: the page the tab LEFT when the agent had
 * looked before the tab rang, the page it CAME TO when the agent first looked after.
 */
function readerWords(what: string, when: TabFact['when'] | null): string {
  const said = what
    .split(' · ')
    .map((part) => {
      switch (part.trim()) {
        case 'the page changed':
          return 'page changed'
        case 'the person clicked':
          return 'you clicked'
        case 'the person typed':
          return 'you typed'
        case 'it went to another page':
          return when === 'before' ? 'left this page' : when === 'after' ? 'came to this page' : 'went to another page'
        default:
          return part.trim()
      }
    })
    .filter(Boolean)
    .join(' · ')
  return said ? said[0]!.toUpperCase() + said.slice(1) : ''
}

/**
 * The two lines of a notice's row, as text — made in ONE place because two things read
 * them: the row that draws them, and the search, which may only find what is drawn.
 */
export function rowWords(act: BrowserActivity, fact: TabFact | null | undefined): { title: string; under: string } {
  const title = fact?.title.trim() || act.tab
  const site = fact?.url ? siteOf(fact.url) : ''
  // The label is how the agent's own commands name the tab; it is not said twice when it
  // is already the title.
  const parts = [readerWords(act.what, fact?.when ?? null), site, act.browser, title === act.tab ? '' : act.tab]
  return { title, under: parts.filter(Boolean).join(' · ') }
}

/**
 * The notices' tabs, for the rows that draw them.
 *
 * A CONTEXT, READ BY THE ROW ALONE. `Msg` is memoized and its props are kept still on
 * purpose (a re-render on every poll is what used to drop a text selection), and this
 * map is new whenever the blocks are. So `Msg` does not read it: the small row it
 * returns for a notice does, and only those rows are drawn again when it moves.
 */
export const TabFactsContext = createContext<ReadonlyMap<TranscriptBlock, TabFact> | null>(null)
